import { ApiError } from '../api/client.ts';
import { isSessionFailure } from './LoadFailure.tsx';

/**
 * How old is what I am looking at.
 *
 * On hotel wifi a refetch can fail while the last good answer is still on
 * screen. Blanking the page would be worse — but so would showing yesterday's
 * flags as though they were current. So the screen always says when it last
 * heard from the server, and says plainly when it is retrying.
 */
export function Staleness(
  { updatedAt, isFetching, error, onRefresh }:
  { updatedAt: number; isFetching: boolean; error: Error | null; onRefresh: () => void },
) {
  /**
   * "Unreachable" and "answered with an error" are different facts.
   *
   * This used to take a boolean and say "Cannot reach the server" for any
   * failure at all — so a 500 from a missing view, and a session that had
   * expired, both reported a network problem, next to a second message saying
   * something else. One cause, two contradictory explanations, and the screen
   * asserting the one thing it could not know.
   *
   * An ApiError means the server replied. Anything else — a TypeError from
   * fetch, a timeout — means nothing came back. And a refused session is
   * neither: NeedsSignIn is not an ApiError, so it used to read as
   * "Cannot reach the server".
   */
  const reason = !error
    ? null
    : isSessionFailure(error, { forbiddenIsSession: false })
      ? 'Session not valid'
      : error instanceof ApiError
      ? 'The server reported an error'
      : 'Cannot reach the server';
  const time = updatedAt
    ? new Date(updatedAt).toLocaleTimeString(undefined, {
        hour: '2-digit', minute: '2-digit',
      })
    : null;

  return (
    <div className="flex min-h-[44px] items-center gap-2 px-4 py-2 text-sm">
      {reason ? (
        <span className="font-semibold text-critical-edge">
          {reason}{time && ` — showing ${time}`}
        </span>
      ) : isFetching ? (
        <span className="text-neutral-500">Updating…</span>
      ) : (
        <span className="text-neutral-500">{time ? `As of ${time}` : 'Loading…'}</span>
      )}
      <button
        type="button"
        onClick={onRefresh}
        className="ml-auto min-h-[44px] rounded-md px-4 font-medium text-neutral-700
                   active:bg-neutral-200"
      >
        Refresh
      </button>
    </div>
  );
}
